import type { ReactNode } from "react";
import { Dialog as RadixDialog } from "radix-ui";
import { AnimatePresence, motion, useDragControls, type PanInfo } from "motion/react";
import { cn } from "./cn";
import { softSpring } from "./motion";
import { useDesktop } from "./useMedia";

export type SheetProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title?: ReactNode;
  className?: string;
  children: ReactNode;
};

export function Sheet({ open, onOpenChange, title, className, children }: SheetProps) {
  const asPanel = useDesktop();
  const drag = useDragControls();

  const hidden = asPanel ? { x: "110%", opacity: 0.6 } : { y: "100%" };
  const shown = asPanel ? { x: 0, opacity: 1 } : { y: 0 };

  function onDragEnd(_: unknown, info: PanInfo) {
    if (info.offset.y > 120 || info.velocity.y > 600) onOpenChange(false);
  }

  return (
    <RadixDialog.Root open={open} onOpenChange={onOpenChange}>
      <AnimatePresence>
        {open ? (
          <RadixDialog.Portal forceMount>
            <RadixDialog.Overlay asChild forceMount>
              <motion.div
                className="fixed inset-0 z-50 bg-accent/30 backdrop-blur-[2px]"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.2 }}
              />
            </RadixDialog.Overlay>
            <RadixDialog.Content
              asChild
              forceMount
              aria-describedby={undefined}
              onOpenAutoFocus={(e) => {
                e.preventDefault();
                (e.currentTarget as HTMLElement).focus();
              }}
            >
              <motion.div
                className={cn(
                  "fixed z-50 flex flex-col bg-surface text-ink shadow-raised outline-none overscroll-contain",
                  asPanel
                    ? "inset-y-0 right-0 w-full max-w-[460px] pr-[env(safe-area-inset-right)] sm:inset-y-2 sm:right-2 sm:w-[calc(100%-1rem)] sm:rounded-xl"
                    : "inset-x-0 bottom-0 max-h-[92dvh] rounded-t-xl pb-[env(safe-area-inset-bottom)]",
                  className,
                )}
                initial={hidden}
                animate={shown}
                exit={hidden}
                transition={softSpring}
                drag={asPanel ? false : "y"}
                dragControls={drag}
                dragListener={false}
                dragConstraints={{ top: 0, bottom: 0 }}
                dragElastic={{ top: 0, bottom: 0.6 }}
                onDragEnd={onDragEnd}
              >
                {asPanel ? null : (
                  <div
                    className={cn("relative flex shrink-0 cursor-grab touch-none items-center justify-center active:cursor-grabbing", title ? "h-6" : "h-11")}
                    onPointerDown={(e) => drag.start(e)}
                  >
                    <span aria-hidden className="h-1 w-10 rounded-full bg-ink/15" />
                    {title ? null : <Close className="absolute right-3 top-0" />}
                  </div>
                )}
                {asPanel || title ? (
                  <div className={cn("flex shrink-0 items-center justify-between gap-3 px-5", asPanel ? cn("h-16", title && "border-b border-line") : "pb-2")}>
                    <RadixDialog.Title className={cn("font-display text-[22px] leading-tight text-balance", !title && "sr-only")}>
                      {title ?? "Details"}
                    </RadixDialog.Title>
                    <Close className="-mr-2 ml-auto" />
                  </div>
                ) : (
                  <RadixDialog.Title className="sr-only">Details</RadixDialog.Title>
                )}
                <div className={cn("min-h-0 flex-1 overflow-y-auto overscroll-contain px-5 pb-5", asPanel || title ? "pt-2" : "pt-1")}>{children}</div>
              </motion.div>
            </RadixDialog.Content>
          </RadixDialog.Portal>
        ) : null}
      </AnimatePresence>
    </RadixDialog.Root>
  );
}

function Close({ className }: { className?: string }) {
  return (
    <RadixDialog.Close
      aria-label="Close"
      onPointerDown={(e) => e.stopPropagation()}
      className={cn(
        "relative grid size-10 shrink-0 place-items-center rounded-control text-muted duration-150 hover:bg-sunk hover:text-ink active:scale-90 transition-[background-color,color,scale]",
        className,
      )}
    >
      <svg viewBox="0 0 16 16" className="size-4" fill="none" aria-hidden>
        <path d="m4 4 8 8M12 4l-8 8" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
      </svg>
    </RadixDialog.Close>
  );
}
